<template>
  <div class="flex flex-col gap-3">
    <div v-for="filter in visibleOpeningTimeFilters" :key="filter.key" class="flex gap-1 items-center">
      <LazyFrontendServiceFilterToggleableToggle v-if="toggleable" :filter-id="filter.filterId" />
      <UCheckbox
        :id="filter.filterId"
        v-model="filter.selected"
        :label="filter.label"
        :ui="{ label: 'font-light' }"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import type { FilterIds } from '~/types/FilterIds'

const { filters, disabledFilters } = storeToRefs(useServiceSearchStore())
const props = defineProps({
  toggleable: Boolean
})

const openingTimesFilters = ref<
  {
    label: string
    key: 'openNow' | 'open247' | 'openWeekends' | 'openEvenings'
    filterId: FilterIds
    selected: boolean
  }[]
>([
  {
    label: 'Open now',
    key: 'openNow',
    selected: false,
    filterId: 'opening-times:open-now'
  },
  {
    label: 'Open 24/7',
    key: 'open247',
    selected: false,
    filterId: 'opening-times:open-247'
  },
  {
    label: 'Open weekends',
    key: 'openWeekends',
    selected: false,
    filterId: 'opening-times:open-weekends'
  },
  {
    label: 'Open evenings',
    key: 'openEvenings',
    selected: false,
    filterId: 'opening-times:open-evenings'
  }
])

const visibleOpeningTimeFilters = computed(() => {
  // if we're in toggleable mode, show all
  if (props.toggleable) return openingTimesFilters.value
  // otherwise, filter out any in the disabled filters array
  // get a ref of the disabled filters.value so we don't need to
  // .value it every iteration of the .filter loop
  const disabled = disabledFilters.value || []
  return openingTimesFilters.value.filter((x) => !disabled.includes(x.filterId))
})

// Before component mounts sync filters with store
// see https://github.com/hiyield/hub-of-hope/pull/735 for why
onBeforeMount(() => {
  for (const filter of openingTimesFilters.value) {
    filter.selected = filters.value[filter.key] ?? false
  }
})

// Watch when filters in store are being reset - uncheck the checkboxes here when filters are cleared
watch(
  filters,
  (newFilters) => {
    for (const filter of openingTimesFilters.value) {
      filter.selected = newFilters[filter.key] ?? false
    }
  },
  { deep: true }
)

// Update filters in store when openingTimesFilters change
watch(
  openingTimesFilters,
  (newOpeningTimesFilters) => {
    for (const filter of newOpeningTimesFilters) {
      filters.value[filter.key] = filter.selected
    }
  },
  { deep: true }
)
</script>
